/**
 * Shopping Gamification Service
 * Smart Purchase Planner 2.0 - Phase 4: Gamification Layer
 * 
 * Provides XP, badges, scores, and streak tracking for shopping behavior
 * FINLIST SSOT: Pure functions, read-only calculations
 */

// ============================================================================
// BADGE DEFINITIONS
// ============================================================================

export const BADGE_CATEGORIES = {
  SAVINGS: 'savings',
  STREAK: 'streak',
  MILESTONE: 'milestone',
  BEHAVIOR: 'behavior',
  SPECIAL: 'special'
};

export const BADGES = {
  // Savings Badges
  PENNY_PINCHER: {
    id: 'penny_pincher',
    name: 'Penny Pincher',
    description: 'Came under budget on a shopping trip',
    emoji: '💰',
    category: BADGE_CATEGORIES.SAVINGS,
    xpReward: 10,
    requirement: { type: 'under_budget', threshold: 1 }
  },
  BUDGET_MASTER: {
    id: 'budget_master',
    name: 'Budget Master',
    description: 'Stayed 20%+ under budget',
    emoji: '🏆',
    category: BADGE_CATEGORIES.SAVINGS,
    xpReward: 25,
    requirement: { type: 'under_budget_percent', threshold: 20 }
  },
  SUPER_SAVER: {
    id: 'super_saver',
    name: 'Super Saver',
    description: 'Saved £50+ on a single trip',
    emoji: '⭐',
    category: BADGE_CATEGORIES.SAVINGS,
    xpReward: 50,
    requirement: { type: 'savings_amount', threshold: 50 }
  },
  FRUGAL_LEGEND: {
    id: 'frugal_legend',
    name: 'Frugal Legend',
    description: 'Saved £500+ lifetime',
    emoji: '👑',
    category: BADGE_CATEGORIES.SAVINGS,
    xpReward: 200,
    requirement: { type: 'lifetime_savings', threshold: 500 }
  },
  
  // Streak Badges
  CONSISTENT_SHOPPER: {
    id: 'consistent_shopper',
    name: 'Consistent Shopper',
    description: '3-day under-budget streak',
    emoji: '🔥',
    category: BADGE_CATEGORIES.STREAK,
    xpReward: 30,
    requirement: { type: 'streak', threshold: 3 }
  },
  WEEK_WARRIOR: {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: '7-day under-budget streak',
    emoji: '💪',
    category: BADGE_CATEGORIES.STREAK,
    xpReward: 75,
    requirement: { type: 'streak', threshold: 7 }
  },
  MONTHLY_CHAMPION: {
    id: 'monthly_champion',
    name: 'Monthly Champion',
    description: '30-day under-budget streak',
    emoji: '🌟',
    category: BADGE_CATEGORIES.STREAK,
    xpReward: 300,
    requirement: { type: 'streak', threshold: 30 }
  },
  
  // Milestone Badges
  FIRST_PURCHASE: {
    id: 'first_purchase',
    name: 'First Purchase',
    description: 'Completed your first shopping trip',
    emoji: '🛒',
    category: BADGE_CATEGORIES.MILESTONE,
    xpReward: 10,
    requirement: { type: 'trips_completed', threshold: 1 }
  },
  SHOPPING_PRO: {
    id: 'shopping_pro',
    name: 'Shopping Pro',
    description: 'Completed 10 shopping trips',
    emoji: '🎯',
    category: BADGE_CATEGORIES.MILESTONE,
    xpReward: 50,
    requirement: { type: 'trips_completed', threshold: 10 }
  },
  CENTURY_CLUB: {
    id: 'century_club',
    name: 'Century Club',
    description: 'Completed 100 shopping trips',
    emoji: '💯',
    category: BADGE_CATEGORIES.MILESTONE,
    xpReward: 500,
    requirement: { type: 'trips_completed', threshold: 100 }
  },
  ITEM_MASTER: {
    id: 'item_master',
    name: 'Item Master',
    description: 'Purchased 500 items total',
    emoji: '📦',
    category: BADGE_CATEGORIES.MILESTONE,
    xpReward: 100,
    requirement: { type: 'items_purchased', threshold: 500 }
  },
  
  // Behavior Badges
  DEAL_HUNTER: {
    id: 'deal_hunter',
    name: 'Deal Hunter',
    description: 'Found 5 items below typical price',
    emoji: '🔍',
    category: BADGE_CATEGORIES.BEHAVIOR,
    xpReward: 25,
    requirement: { type: 'deals_found', threshold: 5 }
  },
  BULK_BUYER: {
    id: 'bulk_buyer',
    name: 'Bulk Buyer',
    description: 'Made a bulk purchase saving 15%+',
    emoji: '📦',
    category: BADGE_CATEGORIES.BEHAVIOR,
    xpReward: 20,
    requirement: { type: 'bulk_savings', threshold: 15 }
  },
  LIST_PERFECTIONIST: {
    id: 'list_perfectionist',
    name: 'List Perfectionist',
    description: 'Bought exactly what was on list - no extras',
    emoji: '✅',
    category: BADGE_CATEGORIES.BEHAVIOR,
    xpReward: 15,
    requirement: { type: 'perfect_list', threshold: 1 }
  },
  EARLY_BIRD: {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Completed shopping before 9 AM',
    emoji: '🌅',
    category: BADGE_CATEGORIES.BEHAVIOR,
    xpReward: 10,
    requirement: { type: 'early_shopping', threshold: 9 }
  },
  
  // Special Badges
  CATEGORY_EXPERT: {
    id: 'category_expert',
    name: 'Category Expert',
    description: 'Stayed under budget in all categories',
    emoji: '🎖️',
    category: BADGE_CATEGORIES.SPECIAL,
    xpReward: 100,
    requirement: { type: 'all_categories_under', threshold: 1 }
  },
  PLANNER_ELITE: {
    id: 'planner_elite',
    name: 'Planner Elite',
    description: 'Actual spend within 5% of estimated',
    emoji: '📊',
    category: BADGE_CATEGORIES.SPECIAL,
    xpReward: 40,
    requirement: { type: 'accurate_estimate', threshold: 5 }
  }
};

// ============================================================================
// XP LEVEL SYSTEM
// ============================================================================

export const XP_LEVELS = [
  { level: 1, name: 'Novice Shopper', minXP: 0, maxXP: 100 },
  { level: 2, name: 'Casual Buyer', minXP: 100, maxXP: 250 },
  { level: 3, name: 'Smart Shopper', minXP: 250, maxXP: 500 },
  { level: 4, name: 'Savvy Saver', minXP: 500, maxXP: 1000 },
  { level: 5, name: 'Budget Expert', minXP: 1000, maxXP: 2000 },
  { level: 6, name: 'Frugal Master', minXP: 2000, maxXP: 4000 },
  { level: 7, name: 'Shopping Sage', minXP: 4000, maxXP: 7500 },
  { level: 8, name: 'Economy Elite', minXP: 7500, maxXP: 12500 },
  { level: 9, name: 'Finance Champion', minXP: 12500, maxXP: 20000 },
  { level: 10, name: 'Legendary Saver', minXP: 20000, maxXP: Infinity }
];

// ============================================================================
// SCORE CALCULATION
// ============================================================================

/**
 * Calculate shopping trip score based on various factors
 * 
 * @param {Object} tripData - Data about the shopping trip
 * @returns {Object} { score, breakdown, xpEarned, badgesEarned }
 */
export function calculateTripScore(tripData) {
  const {
    budgetAmount = 0,
    actualSpent = 0,
    estimatedSpend = 0,
    itemCount = 0,
    purchasedCount = 0,
    dealsFound = 0,
    completedAt = new Date()
  } = tripData;
  
  const result = {
    score: 0,
    maxScore: 100,
    grade: 'F',
    breakdown: [],
    xpEarned: 0,
    newBadges: []
  };
  
  // Base score: 50 points for completing a trip
  let baseScore = 50;
  result.breakdown.push({ label: 'Trip Completed', points: 50, emoji: '🛒' });
  
  // Budget Score (up to 30 points)
  if (budgetAmount > 0 && actualSpent > 0) {
    const budgetRatio = actualSpent / budgetAmount;
    
    if (budgetRatio <= 0.8) {
      // 20%+ under budget = full 30 points
      baseScore += 30;
      result.breakdown.push({ label: '20%+ Under Budget', points: 30, emoji: '🌟' });
    } else if (budgetRatio <= 0.9) {
      // 10-20% under budget = 25 points
      baseScore += 25;
      result.breakdown.push({ label: '10-20% Under Budget', points: 25, emoji: '⭐' });
    } else if (budgetRatio <= 1.0) {
      // Within budget = 20 points
      baseScore += 20;
      result.breakdown.push({ label: 'Within Budget', points: 20, emoji: '✅' });
    } else if (budgetRatio <= 1.1) {
      // 0-10% over budget = 10 points
      baseScore += 10;
      result.breakdown.push({ label: 'Slightly Over Budget', points: 10, emoji: '⚠️' });
    } else {
      // Over 10% over budget = 0 points
      result.breakdown.push({ label: 'Over Budget', points: 0, emoji: '❌' });
    }
  }
  
  // Accuracy Score (up to 15 points)
  if (estimatedSpend > 0 && actualSpent > 0) {
    const accuracyDiff = Math.abs(actualSpent - estimatedSpend) / estimatedSpend;
    
    if (accuracyDiff <= 0.05) {
      // Within 5% of estimate
      baseScore += 15;
      result.breakdown.push({ label: 'Accurate Estimate (±5%)', points: 15, emoji: '🎯' });
    } else if (accuracyDiff <= 0.15) {
      // Within 15%
      baseScore += 10;
      result.breakdown.push({ label: 'Good Estimate (±15%)', points: 10, emoji: '📊' });
    } else if (accuracyDiff <= 0.25) {
      // Within 25%
      baseScore += 5;
      result.breakdown.push({ label: 'Fair Estimate (±25%)', points: 5, emoji: '📈' });
    }
  }
  
  // Completion Score (up to 5 points)
  if (itemCount > 0) {
    const completionRate = purchasedCount / itemCount;
    if (completionRate >= 1.0) {
      baseScore += 5;
      result.breakdown.push({ label: 'All Items Purchased', points: 5, emoji: '💯' });
    } else if (completionRate >= 0.8) {
      baseScore += 3;
      result.breakdown.push({ label: '80%+ Items Purchased', points: 3, emoji: '📝' });
    }
  }
  
  // Bonus: Deals found
  if (dealsFound > 0) {
    const dealBonus = Math.min(dealsFound * 2, 10); // Max 10 bonus points
    baseScore += dealBonus;
    result.breakdown.push({ label: `${dealsFound} Deal${dealsFound > 1 ? 's' : ''} Found`, points: dealBonus, emoji: '🔍' });
  }
  
  // Cap at 100
  result.score = Math.min(baseScore, 100);
  
  // Determine grade
  result.grade = getGradeFromScore(result.score);
  
  // Calculate XP earned
  result.xpEarned = calculateXPFromScore(result.score, tripData);
  
  return result;
}

/**
 * Get letter grade from score
 */
function getGradeFromScore(score) {
  if (score >= 95) return 'S';
  if (score >= 90) return 'A+';
  if (score >= 85) return 'A';
  if (score >= 80) return 'B+';
  if (score >= 75) return 'B';
  if (score >= 70) return 'C+';
  if (score >= 65) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

/**
 * Calculate XP earned from a trip score
 */
function calculateXPFromScore(score, tripData) {
  // Base XP = score / 2
  let xp = Math.floor(score / 2);
  
  // Bonus XP for under-budget
  if (tripData.budgetAmount > 0 && tripData.actualSpent < tripData.budgetAmount) {
    const savingsPercent = ((tripData.budgetAmount - tripData.actualSpent) / tripData.budgetAmount) * 100;
    xp += Math.floor(savingsPercent); // 1 XP per percent saved
  }
  
  // Streak multiplier would be applied here if we had streak data
  
  return xp;
}

// ============================================================================
// LEVEL & XP FUNCTIONS
// ============================================================================

/**
 * Get current level from total XP
 * 
 * @param {number} totalXP - Total XP accumulated
 * @returns {Object} { level, name, currentXP, xpToNextLevel, progress }
 */
export function getLevelFromXP(totalXP) {
  const xp = totalXP || 0;
  
  for (let i = XP_LEVELS.length - 1; i >= 0; i--) {
    const levelData = XP_LEVELS[i];
    if (xp >= levelData.minXP) {
      const xpInLevel = xp - levelData.minXP;
      const xpNeeded = levelData.maxXP - levelData.minXP;
      const progress = levelData.maxXP === Infinity 
        ? 100 
        : Math.round((xpInLevel / xpNeeded) * 100);
      
      return {
        level: levelData.level,
        name: levelData.name,
        currentXP: xp,
        xpInLevel,
        xpToNextLevel: levelData.maxXP === Infinity ? 0 : levelData.maxXP - xp,
        progress,
        nextLevelName: XP_LEVELS[i + 1]?.name || null
      };
    }
  }
  
  return {
    level: 1,
    name: 'Novice Shopper',
    currentXP: xp,
    xpInLevel: xp,
    xpToNextLevel: 100 - xp,
    progress: xp,
    nextLevelName: 'Casual Buyer'
  };
}

// ============================================================================
// STREAK TRACKING
// ============================================================================

/**
 * Calculate streak information from shopping history
 * 
 * @param {Array} shoppingHistory - Array of { date, wasUnderBudget }
 * @returns {Object} { currentStreak, longestStreak, lastTripDate }
 */
export function calculateStreak(shoppingHistory = []) {
  if (!shoppingHistory || shoppingHistory.length === 0) {
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastTripDate: null,
      isActive: false
    };
  }
  
  // Sort by date descending
  const sorted = [...shoppingHistory]
    .filter(h => h.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  if (sorted.length === 0) {
    return { currentStreak: 0, longestStreak: 0, lastTripDate: null, isActive: false };
  }
  
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  
  // Check if streak is still active (last trip within 2 days)
  const lastTripDate = new Date(sorted[0].date);
  const daysSinceLastTrip = Math.floor((Date.now() - lastTripDate.getTime()) / (1000 * 60 * 60 * 24));
  const isActive = daysSinceLastTrip <= 2;
  
  // Count current streak (consecutive under-budget trips)
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].wasUnderBudget) {
      tempStreak++;
      if (i === 0 || (i > 0 && sorted[i - 1].wasUnderBudget)) {
        currentStreak = tempStreak;
      }
    } else {
      tempStreak = 0;
      break; // Streak broken
    }
    longestStreak = Math.max(longestStreak, tempStreak);
  }
  
  // Continue counting for longest streak
  tempStreak = 0;
  for (const trip of sorted) {
    if (trip.wasUnderBudget) {
      tempStreak++;
      longestStreak = Math.max(longestStreak, tempStreak);
    } else {
      tempStreak = 0;
    }
  }
  
  return {
    currentStreak: isActive ? currentStreak : 0,
    longestStreak,
    lastTripDate,
    isActive
  };
}

// ============================================================================
// BADGE CHECKING
// ============================================================================

/**
 * Check which new badges were earned from a shopping trip
 * 
 * @param {Object} tripData - Data about the trip
 * @param {Object} userStats - User's cumulative stats
 * @param {Array} earnedBadges - Previously earned badge IDs
 * @returns {Array} Array of newly earned badge objects
 */
export function checkNewBadges(tripData, userStats = {}, earnedBadges = []) {
  const newBadges = [];
  
  const {
    budgetAmount = 0,
    actualSpent = 0,
    estimatedSpend = 0,
    dealsFound = 0,
    completedAt = new Date()
  } = tripData;
  
  const {
    totalTrips = 0,
    totalItemsPurchased = 0,
    lifetimeSavings = 0,
    currentStreak = 0
  } = userStats;
  
  const earnedSet = new Set(earnedBadges);
  
  // Check each badge
  for (const [key, badge] of Object.entries(BADGES)) {
    if (earnedSet.has(badge.id)) continue; // Already earned
    
    const { type, threshold } = badge.requirement;
    let earned = false;
    
    switch (type) {
      case 'under_budget':
        earned = budgetAmount > 0 && actualSpent < budgetAmount;
        break;
        
      case 'under_budget_percent':
        if (budgetAmount > 0) {
          const percentUnder = ((budgetAmount - actualSpent) / budgetAmount) * 100;
          earned = percentUnder >= threshold;
        }
        break;
        
      case 'savings_amount':
        earned = budgetAmount > 0 && (budgetAmount - actualSpent) >= threshold;
        break;
        
      case 'lifetime_savings':
        earned = lifetimeSavings >= threshold;
        break;
        
      case 'streak':
        earned = currentStreak >= threshold;
        break;
        
      case 'trips_completed':
        earned = totalTrips >= threshold;
        break;
        
      case 'items_purchased':
        earned = totalItemsPurchased >= threshold;
        break;
        
      case 'deals_found':
        earned = dealsFound >= threshold;
        break;
        
      case 'accurate_estimate':
        if (estimatedSpend > 0 && actualSpent > 0) {
          const accuracy = Math.abs(actualSpent - estimatedSpend) / estimatedSpend * 100;
          earned = accuracy <= threshold;
        }
        break;
        
      case 'early_shopping': {
        const hour = completedAt.getHours ? completedAt.getHours() : new Date(completedAt).getHours();
        earned = hour < threshold;
        break;
      }
    }
    
    if (earned) {
      newBadges.push(badge);
    }
  }
  
  return newBadges;
}

/**
 * Get display-ready badge data for earned badges
 * 
 * @param {Array} badgeIds - Array of earned badge IDs
 * @returns {Array} Array of badge objects with display info
 */
export function getEarnedBadges(badgeIds = []) {
  return badgeIds
    .map(id => Object.values(BADGES).find(b => b.id === id))
    .filter(Boolean);
}

/**
 * Get progress towards unearned badges
 * 
 * @param {Object} userStats - User statistics
 * @param {Array} earnedBadges - Already earned badge IDs
 * @returns {Array} Badges with progress percentage
 */
export function getBadgeProgress(userStats, earnedBadges = []) {
  const earnedSet = new Set(earnedBadges);
  const progress = [];
  
  for (const badge of Object.values(BADGES)) {
    if (earnedSet.has(badge.id)) continue;
    
    const { type, threshold } = badge.requirement;
    let current = 0;
    
    switch (type) {
      case 'lifetime_savings':
        current = userStats.lifetimeSavings || 0;
        break;
      case 'streak':
        current = userStats.currentStreak || 0;
        break;
      case 'trips_completed':
        current = userStats.totalTrips || 0;
        break;
      case 'items_purchased':
        current = userStats.totalItemsPurchased || 0;
        break;
      case 'deals_found':
        current = userStats.dealsFound || 0;
        break;
    }
    
    const percent = Math.min(Math.round((current / threshold) * 100), 100);
    
    if (percent > 0) {
      progress.push({
        ...badge,
        current,
        threshold,
        percent
      });
    }
  }
  
  // Sort by closest to completion
  return progress.sort((a, b) => b.percent - a.percent);
}

export default {
  BADGES,
  BADGE_CATEGORIES,
  XP_LEVELS,
  calculateTripScore,
  getLevelFromXP,
  calculateStreak,
  checkNewBadges,
  getEarnedBadges,
  getBadgeProgress
};
